import {
  AssessmentType,
  FlashpackType,
  isInTrialMode,
  isSchoolUserTeacher,
  Lesson,
  UnitWithLessons,
  User,
} from "./api";

export const enum SectionType {
  Generic = 0,
  PreAssessment = 1,
  PostAssessment = 2,
  CustomFlashpacks = 3,
  SharedFlashpacks = 4,
}

export const enum ShowSectionHeader {
  WhenNotEmpty = 1,
  WhenNotFirst = 2,
  WhenNotSingle = 3,
}

export interface SectionWithLessons {
  type: SectionType; // Will be used to generate the React `key` prop besides other logic
  lessons: Lesson[];
  title?: string;
  description?: string;
  showHeader?: ShowSectionHeader;
}

const partitionLessonsIntoSections = (
  t,
  lessons: Lesson[],
  allowCustomFlashpackSection: Boolean
): SectionWithLessons[] => {
  let sections: SectionWithLessons[] = [
    {
      type: SectionType.PreAssessment,
      title: t("unit_details_section_pre_assessments"),
      lessons: [],
      showHeader: ShowSectionHeader.WhenNotEmpty,
    },
    {
      type: SectionType.Generic,
      title: t("unit_details_section_lessons"),
      lessons: [],
      showHeader: ShowSectionHeader.WhenNotSingle,
    },
  ];
  if (allowCustomFlashpackSection) {
    sections.push({
      type: SectionType.CustomFlashpacks,
      title: t("unit_details_section_custom_flashpacks"),
      lessons: [],
      showHeader: ShowSectionHeader.WhenNotFirst,
    });
  }
  sections.push({
    type: SectionType.PostAssessment,
    title: t("unit_details_section_post_assessments"),
    lessons: [],
    showHeader: ShowSectionHeader.WhenNotEmpty,
  });
  for (const lesson of lessons) {
    const index =
      lesson.assessment_type === AssessmentType.PreAssessment
        ? 0
        : lesson.assessment_type === AssessmentType.PostAssessment
        ? allowCustomFlashpackSection
          ? 3
          : 2
        : allowCustomFlashpackSection && lesson.custom_flashpack_id
        ? 2
        : 1;
    sections[index].lessons.push(lesson);
  }
  return sections;
};

const partitionCustomFlashpacksIntoSectionsForTeacher = (
  t,
  user: User,
  lessons: Lesson[]
) => {
  const nonShared: SectionWithLessons = {
    type: SectionType.CustomFlashpacks,
    lessons: [],
  };

  const shared: SectionWithLessons = {
    type: SectionType.SharedFlashpacks,
    title: t("unit_details_shared_flashpacks_title"),
    description: t("unit_details_shared_flashpacks_description"),
    lessons: [],
    showHeader: ShowSectionHeader.WhenNotEmpty,
  };

  for (const lesson of lessons) {
    if (lesson.owner_id === user.id) {
      nonShared.lessons.push(lesson);
    }
    if (lesson.shared) {
      shared.lessons.push(lesson);
    }
  }
  shared.lessons.sort((a, b) => b.shared_timestamp! - a.shared_timestamp!);

  return [nonShared, shared];
};

export const getSectionsFromUnit = (
  t,
  user: User,
  unit: UnitWithLessons
): SectionWithLessons[] => {
  if (unit.flashpack_type === FlashpackType.Custom) {
    if (isSchoolUserTeacher(user)) {
      // For School user teachers, we split custom flashpacks into "my flashpacks" and "shared with me" sections
      return partitionCustomFlashpacksIntoSectionsForTeacher(
        t,
        user,
        unit.lessons
      );
    } else {
      // For non-teachers, we show all custom flashpacks in one section
      return [
        {
          type: SectionType.CustomFlashpacks,
          lessons: unit.lessons,
        },
      ];
    }
  } else if (unit.supports_flashpack_creation) {
    // For units that support flashpack creation, we allow the inclusion of custom flashpacks.
    return partitionLessonsIntoSections(t, unit.lessons, !isInTrialMode(user));
  } else {
    // For all non-custom-flashpacks units, we split lessons into assessments vs non-assessments sections
    return partitionLessonsIntoSections(t, unit.lessons, false);
  }
};
